"use client"

import { Suspense, useEffect, useRef, useState } from "react"
import { usePathname, useSearchParams } from "next/navigation"
import { useLoadingStore } from "@/stores/loading-store"

function LoadingBarContent() {
  const isLoading = useLoadingStore((state) => state.isLoading)
  const setLoading = useLoadingStore((state) => state.setLoading)
  const targetRoute = useLoadingStore((state) => state.targetRoute)
  const pathname = usePathname()
  const searchParams = useSearchParams()

  const [animateFullWidth, setAnimateFullWidth] = useState(false)
  const loadingInitiatedRef = useRef<{ route: string | null; time: number }>({ route: null, time: 0 })
  const completionTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const safetyTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    return () => {
      if (completionTimeoutRef.current) {
        clearTimeout(completionTimeoutRef.current)
      }
      if (safetyTimeoutRef.current) {
        clearTimeout(safetyTimeoutRef.current)
      }
    }
  }, [])

  useEffect(() => {
    const currentRoute = `${pathname}${searchParams.toString() ? `?${searchParams.toString()}` : ""}`
    const routesMatch =
      targetRoute && (targetRoute === currentRoute || (targetRoute === pathname && currentRoute === pathname))

    if (completionTimeoutRef.current && !animateFullWidth) {
      clearTimeout(completionTimeoutRef.current)
      completionTimeoutRef.current = null
    }

    if (!isLoading || !targetRoute) {
      setAnimateFullWidth(false)
      loadingInitiatedRef.current = { route: null, time: 0 }
      if (completionTimeoutRef.current) {
        clearTimeout(completionTimeoutRef.current)
        completionTimeoutRef.current = null
      }
      if (safetyTimeoutRef.current) {
        clearTimeout(safetyTimeoutRef.current)
        safetyTimeoutRef.current = null
      }
      return
    }

    if (loadingInitiatedRef.current.route !== targetRoute) {
      loadingInitiatedRef.current = { route: targetRoute, time: Date.now() }
      setAnimateFullWidth(false)
      if (completionTimeoutRef.current) {
        clearTimeout(completionTimeoutRef.current)
        completionTimeoutRef.current = null
      }
      if (safetyTimeoutRef.current) {
        clearTimeout(safetyTimeoutRef.current)
      }
      safetyTimeoutRef.current = setTimeout(() => {
        setLoading(false)
        safetyTimeoutRef.current = null
      }, 5000)
    }

    if (routesMatch) {
      const timeElapsed = Date.now() - loadingInitiatedRef.current.time
      if (timeElapsed < 200) {
        if (completionTimeoutRef.current) clearTimeout(completionTimeoutRef.current)
        if (safetyTimeoutRef.current) {
          clearTimeout(safetyTimeoutRef.current)
          safetyTimeoutRef.current = null
        }
        setLoading(false)
        setAnimateFullWidth(false)
      } else {
        if (!animateFullWidth && !completionTimeoutRef.current) {
          setAnimateFullWidth(true)
          completionTimeoutRef.current = setTimeout(() => {
            setLoading(false)
            if (safetyTimeoutRef.current) {
              clearTimeout(safetyTimeoutRef.current)
              safetyTimeoutRef.current = null
            }
            completionTimeoutRef.current = null
          }, 300)
        }
      }
    } else {
      if (animateFullWidth) {
        setAnimateFullWidth(false)
        if (completionTimeoutRef.current) {
          clearTimeout(completionTimeoutRef.current)
          completionTimeoutRef.current = null
        }
      }
    }
  }, [isLoading, targetRoute, pathname, searchParams, setLoading, animateFullWidth])

  if (!isLoading) {
    return null
  }

  return (
    <div className="fixed inset-x-0 top-0 z-50 h-[3px]">
      <div
        className={`bg-primary animate-loading h-full rounded-r-full ${animateFullWidth ? "w-full duration-300" : ""}`}
      />
    </div>
  )
}

export default function LoadingBar() {
  return (
    <Suspense>
      <LoadingBarContent />
    </Suspense>
  )
}
